/**
 * Workforce helpers — pure derivations for the Dashboard's "Your AI
 * workforce" strip.
 *
 * The strip answers the operator's actual question — "what do these agents
 * even DO?" — so every deployed agent gets a plain-English one-liner of its
 * job (`agentPurpose`), an honest last-run label from the real record
 * (`lastRunLabel`, '—' when it has never run), and a live count of ITS
 * proposals waiting in the approvals inbox (`pendingProposalsFor`).
 *
 * Attribution is by action DOMAIN, mirroring the deliberate split in
 * `db/agents.ts` (each template owns one action namespace so two agents
 * never double up):
 *
 *   outreach-monitor  → `schedule_follow_up` actions
 *   ticket-worker     → `set_ticket_status` actions
 *   task-router       → `assign_ticket` actions
 *   system-scanner    → `create_task` in the revenue-title namespace
 *                       ("Start renewal — …" / "Collect past-due payment…" /
 *                        "Confirm upcoming payment…")
 *   customer-success  → `create_task` in the check-in namespace ("Check in — …")
 *   document-chase    → `create_task` in the document namespace
 *                       ("Re-issue or close …" / "Nudge client on …")
 *
 * Templates that never queue governed proposals honestly count 0. PURE by
 * contract: no store reads, no clock reads unless `now` is omitted.
 */

import type {
  AgentTemplateId,
  ApprovalItem,
  AgentRunState,
  DeployedAgent,
} from '../db/types';
import { fmtNextRun } from './agentScheduler';

/** Plain-English "what it does" one-liner per agent template. Total record —
 *  adding a template id without a purpose line is a compile error. */
export const AGENT_PURPOSE: Record<AgentTemplateId, string> = {
  'outreach-monitor':
    'Watches prospects going quiet and drafts re-engagement follow-ups',
  'system-scanner':
    'Sweeps every record for risks: overdue invoices, renewals, stuck work',
  'ticket-worker':
    'Watches the ticket queue and escalates overdue work that never got started',
  'task-router':
    'Routes unowned priority tickets to whoever is carrying the lightest load',
  'customer-success':
    'Scores every live customer’s health and proposes check-ins when accounts slip',
  'document-chase':
    'Chases lapsed proposals and contracts stuck waiting on the client',
  researcher: 'Investigates anomalies and spikes, then writes up what it found',
  'market-research':
    'Tracks competitors, pricing moves, and market shifts, and reports what changed',
  'icp-builder':
    'Studies your best customers and keeps the ideal-customer profile current',
  'web-scraper': 'Pulls and structures data from target websites on a schedule',
  'linkedin-monitor':
    'Watches prospect LinkedIn activity for buying signals worth acting on',
  'x-monitor': 'Tracks mentions and conversations about you on X',
  'six-sigma-analyst':
    'Hunts for process variance and flags improvement opportunities',
  'content-writer': 'Drafts on-brand posts, emails, and proposals for review',
  'outreach-sequencer': 'Builds and paces multi-step outreach sequences',
  'deadline-watcher': 'Watches due dates and raises a hand before they slip',
  'standup-reporter': 'Compiles what moved yesterday into a daily standup brief',
  custom: 'Custom agent — does whatever its description says, under approval',
};

/** The plain-English job description for a template. */
export function agentPurpose(template: AgentTemplateId): string {
  return AGENT_PURPOSE[template];
}

// Title namespaces for the shared `create_task` proposal pool — the same
// deliberate namespacing the producer agents de-dupe on.
const REVENUE_TITLE = /^(Start renewal — |Collect past-due payment|Confirm upcoming payment)/;
const CHECKIN_TITLE = /^Check in — /;
const DOC_CHASE_TITLE = /^(Re-issue or close |Nudge client on )/;

/** Rows that are still awaiting a human decision. */
function pendingRows(approvals: readonly ApprovalItem[]): ApprovalItem[] {
  return approvals.filter((a) => a.status === 'pending');
}

/**
 * How many PENDING approvals belong to this template's action domain.
 * Honest zero for templates that never queue governed proposals — the UI
 * shows the truth, not a flattering number.
 */
export function pendingProposalsFor(
  template: AgentTemplateId,
  approvals: readonly ApprovalItem[],
): number {
  const pending = pendingRows(approvals);
  switch (template) {
    case 'outreach-monitor':
      return pending.filter((a) => a.action.kind === 'schedule_follow_up').length;
    case 'ticket-worker':
      return pending.filter((a) => a.action.kind === 'set_ticket_status').length;
    case 'task-router':
      return pending.filter((a) => a.action.kind === 'assign_ticket').length;
    case 'system-scanner':
      return pending.filter(
        (a) => a.action.kind === 'create_task' && REVENUE_TITLE.test(a.action.title),
      ).length;
    case 'customer-success':
      return pending.filter(
        (a) => a.action.kind === 'create_task' && CHECKIN_TITLE.test(a.action.title),
      ).length;
    case 'document-chase':
      return pending.filter(
        (a) => a.action.kind === 'create_task' && DOC_CHASE_TITLE.test(a.action.title),
      ).length;
    default:
      return 0;
  }
}

/**
 * Honest last-run label from the agent record's real `lastRunAt`:
 * '—' when it has never run, otherwise a compact relative time
 * ("just now" / "12m ago" / "3h ago" / "2d ago"). `now` is injectable
 * for deterministic tests; an unparseable timestamp comes back verbatim
 * rather than pretending to a relative time.
 */
export function lastRunLabel(lastRunAt: string | null, now = Date.now()): string {
  if (!lastRunAt) return '—';
  const t = Date.parse(lastRunAt);
  if (Number.isNaN(t)) return lastRunAt;
  const mins = Math.floor(Math.max(0, now - t) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * Honest "when will this run again?" line for an agent card.
 *
 * The bug this fixes: a card would show 'Next: Due now' forever, because the
 * only thing that ADVANCES an overdue agent is the app-wide scheduler loop —
 * and that loop only runs while the master autonomy switch is ON. With
 * autonomy OFF (the default), a running, non-manual agent is "due" but
 * nothing will ever pick it up, so a 'Next:' time is a lie.
 *
 * So a real 'Next: …' is only returned when something will ACTUALLY run the
 * agent on its own: autonomy is on AND the agent is running on a non-manual
 * cadence. Otherwise we tell the truth — it only moves when you click Run now.
 *
 * PURE: `now` is injectable; no store or clock reads unless `now` is omitted.
 */
export interface NextRunDescription {
  /** True when a real, scheduled 'Next:' time is shown. */
  scheduled: boolean;
  /** The label to render, e.g. 'Next: in 12m' or the paused explainer. */
  label: string;
}

/** The honest paused explainer, shared so tests + UI agree on the wording. */
export const PAUSED_NEXT_RUN_LABEL = 'Paused — runs when you click Run now';

export function describeNextRun(
  agent: DeployedAgent,
  autonomyOn: boolean,
  now = Date.now(),
): NextRunDescription {
  const willAutoRun =
    autonomyOn && agent.state === 'running' && agent.cadence !== 'manual';
  if (!willAutoRun) {
    return { scheduled: false, label: PAUSED_NEXT_RUN_LABEL };
  }
  return { scheduled: true, label: `Next: ${fmtNextRun(agent, now)}` };
}

/** The strip's per-agent card model — exactly what WorkforceStrip renders. */
export interface WorkforceCard {
  id: string;
  name: string;
  template: AgentTemplateId;
  /** Plain-English "what it does" one-liner. */
  purpose: string;
  /** Honest last-run label ('—' when never run). */
  lastRun: string;
  /** PENDING approvals in this agent's action domain. */
  pending: number;
  /** True when the agent is currently running. */
  running: boolean;
  /** Lifecycle state, straight from the record. */
  state: AgentRunState;
}

/**
 * Map the REAL deployed-agents source (the `agents` collection AgentsView
 * lists) + the approvals inbox into the strip's card model, so Home and the
 * Agents view can never disagree about who is deployed. PURE: `now` injectable.
 */
export function toWorkforceCards(
  agents: readonly DeployedAgent[],
  approvals: readonly ApprovalItem[],
  now = Date.now(),
): WorkforceCard[] {
  return agents.map((agent) => ({
    id: agent.id,
    name: agent.name,
    template: agent.template,
    purpose: agentPurpose(agent.template),
    lastRun: lastRunLabel(agent.lastRunAt, now),
    pending: pendingProposalsFor(agent.template, approvals),
    running: agent.state === 'running',
    state: agent.state,
  }));
}
